import { isNil } from 'lodash';
import { clampPercentage, clampValue } from '../geometry/math';

const FULL_CIRCLE_DEGREES = 360;
const CIRCULAR_PROGRESS_OFFSET_DEGREES = 90; // Rotate to start from 12 o'clock

export const PERCENTAGE_MULTIPLIER = 100;
export const DEFAULT_START_TIME = 0;

interface TrimBoundariesInput {
  startTime?: number;
  endTime?: number;
  duration: number;
}

const resolveStartTime = (startTime?: number) => (isNil(startTime) ? DEFAULT_START_TIME : startTime);

const resolveEndTime = (endTime: number | undefined, duration: number) => (isNil(endTime) ? duration : endTime);

export const resolveTrimBoundaries = ({ startTime, endTime, duration }: TrimBoundariesInput) => {
  const start = clampValue(resolveStartTime(startTime), { min: 0, max: duration });
  const end = clampValue(resolveEndTime(endTime, duration), { min: 0, max: duration });

  if (end < start) {
    return { start, end: start };
  }
  return {
    start,
    end,
  };
};

export const calculateTrimmedFragmentDuration = (input: TrimBoundariesInput) => {
  const { start, end } = resolveTrimBoundaries(input);
  return Math.max(0, end - start);
};

/**
 * Calculates the percentage of the trimmed segment that the current time corresponds to
 */
export const calculatePercentageFromTime = ({
  currentTime,
  ...trimInput
}: TrimBoundariesInput & { currentTime: number }) => {
  const { start, end } = resolveTrimBoundaries(trimInput);
  const trimmedDuration = Math.max(0, end - start);

  if (trimmedDuration === 0) {
    return 0;
  }

  const clampedTime = clampValue(currentTime, { min: start, max: end });
  const percentage = ((clampedTime - start) / trimmedDuration) * PERCENTAGE_MULTIPLIER;

  return clampPercentage(percentage);
};

/**
 * Calculates the time that corresponds to the given percentage of the trimmed segment
 */
export const calculateTimeFromPercentage = ({
  percentage,
  ...trimInput
}: TrimBoundariesInput & { percentage: number }) => {
  const { start, end } = resolveTrimBoundaries(trimInput);
  const trimmedDuration = Math.max(0, end - start);

  if (trimmedDuration === 0) {
    return start;
  }

  const clampedPercentage = clampPercentage(percentage);
  const timeWithinTrim = (clampedPercentage / PERCENTAGE_MULTIPLIER) * trimmedDuration;
  const time = start + timeWithinTrim;

  return clampValue(time, { min: start, max: end });
};

export const clampTimeToTrim = ({ time, ...trimInput }: TrimBoundariesInput & { time: number }) => {
  const { start, end } = resolveTrimBoundaries(trimInput);
  return clampValue(time, { min: start, max: end });
};

export const calculateLinearPercentageFromClientX = ({
  clientX,
  rect,
}: {
  clientX: number;
  rect: Pick<DOMRect, 'left' | 'width'>;
}) => {
  if (rect.width <= 0) {
    return 0;
  }

  const position = clientX - rect.left;
  const rawProgress = (position / rect.width) * PERCENTAGE_MULTIPLIER;

  return clampPercentage(rawProgress);
};

export const calculateCircularProgressFromClientPoint = ({
  clientX,
  clientY,
  rect,
}: {
  clientX: number;
  clientY: number;
  rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>;
}) => {
  const centerX = rect.width / 2;
  const centerY = rect.height / 2;

  const relativeX = clientX - rect.left - centerX;
  const relativeY = clientY - rect.top - centerY;

  let angle = Math.atan2(relativeY, relativeX);

  angle = (angle * 180) / Math.PI;
  angle = (angle + CIRCULAR_PROGRESS_OFFSET_DEGREES + FULL_CIRCLE_DEGREES) % FULL_CIRCLE_DEGREES;

  const progress = (angle / FULL_CIRCLE_DEGREES) * PERCENTAGE_MULTIPLIER;

  return clampPercentage(progress);
};
